import {Component, OnInit, ViewChild, ɵCodegenComponentFactoryResolver} from '@angular/core';
import {merge, Observable, Subject, forkJoin, combineLatest, Subscription, ObjectUnsubscribedError, throwError} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, map, skip, take, catchError, retry} from 'rxjs/operators';
import {Station} from './models/station.model';
import {NgbCalendar, NgbDate, NgbTypeahead} from '@ng-bootstrap/ng-bootstrap';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {ScienceApiService} from './services/science-api.service';
import {HttpCancelService} from './services/http-cancel.service';
import {SoilService} from './services/soil.service';
import {ChartService} from "./services/chart.service";
import { HttpErrorResponse } from '@angular/common/http';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  public title = 'Soil Water Tool';
  public stations: any;
  public station: Station;
  public ready: boolean;
  public loading: boolean;
  public complete: boolean;
  public loadError: null;
  public error: null;
  public etoCollapsed: boolean;
  public result: any;
  public soil: any;
  public soilTypes: any[];
  public chart: any;

  //  Date Now
  public now: Date;
  public year: number;
  public month: number;
  public day: number;

  // Season start and end dates.
  public startDateDefault: NgbDate;
  public startDate: NgbDate;
  public startMaxDate: NgbDate;
  public startMinDate: NgbDate;
  public endDateDefault: NgbDate;
  public endDate: NgbDate;
  public endMaxDate: NgbDate;
  public endMinDate: NgbDate;

  public initDays: any;
  public devDays: any;
  public midDays: any;
  public lateDays: any;
  public initCoef: any;
  public devCoef: any;
  public midCoef: any;
  public lateCoef: any;
  public break1threshold: any;
  public break2threshold: any;

  public defaultSoil: String;

  public obs: Subscription; 

  /**
   *
   * @param calendarService NgBootstrap Calendar service
   * @param stationService DPIRD weather station API service
   * @param api Science API service
   * @param router Angular router
   * @param route The activated route
   * @param httpCancelService interceptor for if the user cancels the api call.
   * @param soilService
   * @param groundCoverService
   * @param climateService
   * @param soilWaterModelService
   * @param chartService
   */
  constructor(private calendarService: NgbCalendar,
              private scienceApiService: ScienceApiService,
              private api: ScienceApiService,
              private router: Router,
              private route: ActivatedRoute,
              private httpCancelService: HttpCancelService,
              private soilService: SoilService,
              private chartService: ChartService) {
  }

  /**
   * ngOnInit.
   */
  ngOnInit() {
    this.ready = false;
    this.etoCollapsed = true;
    this.initialiseDefaults();
    this.loadInitialData();
  }

  /**
   * Set a default station.
   */
  setStation = (stationCode: string) => {
    // Set default station
    for (const station of this.stations) {
      if (station.stationCode === stationCode) {
        this.station = station;
        break;
      }
    }
  };

  initialiseDefaults = () => {
    // Prep dates.
    this.now = new Date();
    this.year = this.now.getFullYear();
    this.month = this.now.getMonth() + 1;
    this.day = this.now.getDate();
    this.defaultSoil = 'sand';

    this.startDateDefault = new NgbDate(this.year - 1, 11, 1);
    this.endDateDefault = new NgbDate(this.year, 10, 31);
    this.endMaxDate = new NgbDate(this.year+1, this.month, this.day);

    this.initDays = {min: 0, max: 150, value: 15, default: 15};
    this.devDays = {min: 0, max: 150, value: 20, default: 20};
    this.midDays = {min: 0, max: 150, value: 80, default: 80};
    this.lateDays = {min: 0, max: 150, value: 45, default: 45};
    this.initCoef = {min: 0, max: 1, value: 0.05, default: 0.05};
    this.devCoef = {min: 0, max: 1, value: 0.1, default: 0.1};
    this.midCoef = {min: 0, max: 2, value: 0.45, default: 0.45};
    this.lateCoef = {min: 0, max: 1, value: 0.2, default: 0.2};
    this.break1threshold = {min: 0, max: 50, value: 15, default: 50};
    this.break2threshold = {min: 0, max: 20, value: 5, default: 20};
  };

  loadInitialData = () => {
    const queryObs = this.route.queryParams;
    const stationsObs = this.scienceApiService.getStationList()
      .pipe(
        map((stations => stations.collection.map(station => new Station().deserialize(station))
      )));

    this.soilTypes = this.soilService.getSoils();

    this.obs = combineLatest([queryObs, stationsObs])
      .pipe(
        take(1),
        retry(1),
      )
      .subscribe(
        data => {
          const params: any = data[0];
          this.stations = data[1];
          if (params.stationCode) {
            this.setStation(params.stationCode);
          } else {
            // Default station.
            this.setStation('9500'); // Albany.
          }
          this.ready = true;

          if (!this.isEmpty(params)) {
            if (!this.isEmpty(params.devCoef)) {
              this.etoCollapsed = true;
            }
            if (!this.isEmpty(params.initDays)) {
              this.initDays.value = params.initDays;
            }
            if (!this.isEmpty(params.devDays)) {
              this.devDays.value = params.devDays;
            }
            if (!this.isEmpty(params.midDays)) {
              this.midDays.value = params.midDays;
            }
            if (!this.isEmpty(params.lateDays)) {
              this.lateDays.value = params.lateDays;
            }
            if (!this.isEmpty(params.initCoef)) {
              this.initCoef.value = params.initCoef;
            }
            if (!this.isEmpty(params.devCoef)) {
              this.devCoef.value = params.devCoef;
            }
            if (!this.isEmpty(params.midCoef)) {
              this.midCoef.value = params.midCoef;
            }
            if (!this.isEmpty(params.lateCoef)) {
              this.lateCoef.value = params.lateCoef;
            }
            if (!this.isEmpty(params.bos1)) {
              this.break1threshold.value = params.bos1;
            }
            if (!this.isEmpty(params.bos2)) {
              this.break2threshold.value = params.bos2;
            }

            this.soil = params.soil;

            this.startDate = (params.startDate)
              ? this.stringToDate(params.startDate, '/')
              : this.startDateDefault;
            this.startMaxDate = new NgbDate(this.year + 2, 12, 31);
            this.startMinDate = new NgbDate(1975, 1, 1);
            this.endDate = (params.endDate) ? this.stringToDate(params.endDate, '/') : this.endDateDefault;
            this.endMaxDate = new NgbDate(this.year + 2, 12, 31);
            this.endMinDate = new NgbDate(1975, 1, 2);
          } else {
            this.startDate = this.startDateDefault;
            this.endDate = this.endDateDefault;
            this.soil = this.defaultSoil;
          }
        },
        error => {
          this.loadError = error.message;
        }
      );
  }

  /**
   * Run the model.
   */
  calculate = () => {
    //This looks a bit strange but when the endDate is changed (not using the datepicker)
    //the end date is converted from a NgbDate to an object and this breaks later functions.
    this.endDate = new NgbDate(this.endDate.year, this.endDate.month, this.endDate.day);

    this.loading = true;
    this.urlUpdate();
    const startDateStr = this.startDate.year + '-'
      + ('0' + this.startDate.month.toString()).slice(-2) + '-'
      + ('0' + this.startDate.day.toString()).slice(-2);
    const endDateStr = this.endDate.year + '-'
      + ('0' + this.endDate.month.toString()).slice(-2) + '-'
      + ('0' + this.endDate.day.toString()).slice(-2);

    let evap = {
      'initDays': this.initDays.value,
      'devDays': this.devDays.value,
      'midDays': this.midDays.value,
      'lateDays': this.lateDays.value,
      'initCoef': this.initCoef.value,
      'devCoef': this.devCoef.value,
      'midCoef': this.midCoef.value,
      'lateCoef': this.lateCoef.value,
      'bos1': this.break1threshold.value,
      'bos2': this.break2threshold.value
    };

    this.api.getSoilWater(startDateStr, endDateStr, this.soil, this.station.stationCode, evap).subscribe(data => {
      const niceName = this.soilTypes.filter(function(entry, ) { return entry.id === this.soil }, this)[0].nice;
      const title = 'Soil Water ' + this.station.stationName + ' - ' + niceName;
      let rainfall = [];
      const crop = [];
      const fallow = [];

      for (const record of data.data.soilWater) {
        const dateUTC = this.dateStringToUTC(record.date, '-');
        rainfall.push([dateUTC, record.rainfall]);
        crop.push([dateUTC, record.crop]);
        fallow.push([dateUTC, record.fallow]);
      }

      // If the end date is after today then fill the blanks
      const today = new NgbDate(this.year, this.month, this.day);
      if (this.endDate.after(today)) {
        let nulls = this.generateSeasonalNulls(today, this.endDate);
        rainfall = rainfall.concat(nulls);
      }
      let bos = null;
      let bosText = null;
      if (data.data.breakOfSeason !== null) {
        bos = this.dateStringToUTC(data.data.breakOfSeason, '-');
        const d = new Date(data.data.breakOfSeason);
        bosText = 'Break: ' + d.toLocaleDateString("en-AU", {month: 'short', day: 'numeric'});
      }
      this.chart = this.chartService.getChart(rainfall, crop, fallow, bos, bosText, title);
      this.loading = false;
      this.complete = true;
    },
      error => {
        this.error = error.message;
    });
  };

  urlUpdate = () => {
    const queryParams: Params = {};
    queryParams.initDays = this.initDays.value;
    queryParams.devDays = this.devDays.value;
    queryParams.midDays = this.midDays.value;
    queryParams.lateDays = this.lateDays.value;
    queryParams.initCoef = this.initCoef.value;
    queryParams.devCoef = this.devCoef.value;
    queryParams.midCoef = this.midCoef.value;
    queryParams.lateCoef = this.lateCoef.value;
    queryParams.bos1 = this.break1threshold.value;
    queryParams.bos2 = this.break2threshold.value;

    if (!this.isEmpty(this.station)) {
      queryParams.stationCode = this.station.stationCode;
    }
    if (this.startDate) {
      queryParams.startDate = this.dateToString(this.startDate, '/');
    }
    if (this.endDate) {
      queryParams.endDate = this.dateToString(this.endDate, '/');
    }
    if (this.soil) {
      queryParams.soil = this.soil;
    }
    this.router.navigate(['/'], {queryParams: queryParams, relativeTo: this.route});
  };

  /**
   * Reset date fields to the default.
   */
  reset = () => {
    this.startDate = this.startDateDefault;
    this.endDate = this.endDateDefault;
    this.soil = this.defaultSoil;
    this.initDays.value = this.initDays.default;
    this.devDays.value = this.devDays.default;
    this.midDays.value = this.midDays.default;
    this.lateDays.value = this.lateDays.default;
    this.initCoef.value = this.initCoef.default;
    this.devCoef.value = this.devCoef.default;
    this.midCoef.value = this.midCoef.default;
    this.lateCoef.value = this.lateCoef.default;
    this.break1threshold.value = this.break1threshold.default;
    this.break2threshold.value = this.break2threshold.default;
  };

  /**
   * Cancel the current load
   */
  cancel = () => {
    this.obs.unsubscribe();
    this.httpCancelService.cancelPendingRequests();
    this.loading = false;
    this.error = null;
    this.ready = true;
  };


  /**
   * Remove a year from each date fields.
   */
  subtractYear = () => {
    this.startDate = new NgbDate(this.startDate.year - 1, this.startDate.month, this.startDate.day);
    this.endDate = new NgbDate(this.endDate.year - 1, this.endDate.month, this.endDate.day);
    this.urlUpdate();
  };

  /**
   * Add a year to each date field.
   */
  addYear = () => {

    const newEndDate = new NgbDate(this.endDate.year + 1, this.endDate.month, this.endDate.day);
    const currentDate = new NgbDate(this.year, this.month, this.day);

    const newStartDate = new NgbDate(this.startDate.year + 1, this.startDate.month, this.startDate.day);

    if(newStartDate.after(currentDate)){
      return;
    }

    // Don't allow the user to set the end date past the max end date.
    if (newEndDate.year < this.endMaxDate.year && newStartDate.before(newEndDate)) {
      this.endDate = newEndDate;
      this.startDate = newStartDate;
    }
    this.urlUpdate();
  };

  /**
   * If run before the start of the season, we need to supply an array
   * of nulls to the chart.
   */
  generateSeasonalNulls = (startDate, endDate) => {
    const arr = [];
    for (let date = startDate; date.before(endDate); date = this.calendarService.getNext(date, 'd', 1)) {
      arr.push([this.ngbDateToUTC(date), null]);
    }
    return arr;
  };

  @ViewChild('instance', {static: true})
  instance: NgbTypeahead;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  /**
   * Format the text in the station select textfield.
   * @param result
   */
  stationInputFormatter = (result: Station) => this.ucwords(result.stationName.toLowerCase());

  /**
   * Allow scrolling in the typeahead box when using the keyboard.
   */
  typeaheadKeydown() {
    if (!this.instance.isPopupOpen()) {
      return;
    }
    setTimeout(() => {
      const popup = document.getElementById(this.instance.popupId),
        activeElements = popup.getElementsByClassName('active');
      if (activeElements.length === 1) {
        const elem = activeElements[0] as HTMLElement;
        elem.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    });
  }

  /**
   * Uppercase the first letter of each word in a string.
   * @param str
   */
  ucwords = (str: string) => {
    return (str + '')
      .replace(/^(.)|\s+(.)/g, function ($1) {
        return $1.toUpperCase();
      });
  };

  /**
   * Typeahead triggered when text is entered into the station select textfield. Filters the list of stations.
   * @param text$
   */
  search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map(term => (term === '' ? this.stations
        : this.stations.filter(
          v => v.stationName.concat(v.stationCode).concat(v.owner).toLowerCase().indexOf(term.toLowerCase()) > -1))
      ));
  };

  /***
   * Converts a date in string format 'YYYY-MM-DD' to UTC.
   * @param date in format 'YYYY-MM-DD'
   * @param separator the thing between the date bits.
   */
  dateStringToUTC = (date: string, separator: string) => {
    const [year, month, day] = date.split(separator);
    const dateObject = new Date(Date.UTC(year as any, month as any - 1, day as any));
    return dateObject.getTime();
  };

  /**
   * Convert a datePicker date object into a string. Used to translate query parameters.
   * @param d
   * @param separator
   */
  dateToString = (d, separator: string = '-') => {
    return d.day + separator + d.month + separator + d.year;
  };

  /**
   * @param s
   * @param separator
   */
  stringToDate = (s, separator: string) => {
    const arr = s.split(separator);
    return new NgbDate(+arr[2], +arr[1], +arr[0]);
  };

  ngbDateToUTC = (date: NgbDate) => {
    let utc = new Date(Date.UTC(date.year as any, date.month as any, date.day as any));
    return utc.getTime();
  };

  /**
   * Check if an object is empty.
   * @param obj the object to check
   */
  isEmpty = (obj) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  };

}

